import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Phone, HeartHandshake, BookOpen } from "lucide-react";
import { LogoMark } from "@/components/LogoMark";
import { EASE, MaskedLine, Reveal } from "@/components/motion";
import { FreeBookClaim } from "@/components/FreeBookClaim";
import { ContactButton } from "@/components/ContactDialog";

const Header = () => (
  <motion.header
    className="sticky top-0 z-50 w-full bg-black border-b-2 border-white/20 text-white"
    data-testid="poker-header"
    initial={{ y: -80 }}
    animate={{ y: 0 }}
    transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
  >
    <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 lg:px-12 py-4">
      <Link to="/" className="flex items-center gap-3" data-testid="poker-logo-link">
        <LogoMark size={30} dark />
        <span className="font-display text-base sm:text-2xl uppercase tracking-tight whitespace-nowrap">
          The Last <span className="text-[#E60000]">Bet</span> Project
        </span>
      </Link>
      <nav className="hidden md:flex items-center gap-8 text-sm font-semibold uppercase tracking-widest">
        <a href="#signs" data-testid="poker-nav-signs" className="hover:text-[#E60000] transition-colors">The Signs</a>
        <a href="#help" data-testid="poker-nav-help" className="hover:text-[#E60000] transition-colors">Get Help</a>
        <Link to="/gambling" data-testid="poker-nav-gambling" className="hover:text-[#E60000] transition-colors">Gambling Project</Link>
      </nav>
      <div className="flex items-center gap-3">
        <ContactButton dark iconOnly />
        <Link
          to="/"
          data-testid="poker-back-link"
          className="inline-flex items-center gap-2 border-2 border-white bg-transparent px-4 py-2 font-bold uppercase tracking-widest text-xs hover:bg-[#E60000] hover:border-[#E60000] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Main Site
        </Link>
      </div>
    </div>
  </motion.header>
);

const HERO_LINES = [
  { text: "Poker didn't", accent: false },
  { text: "beat you.", accent: false },
  { text: "It hooked you.", accent: true },
];

const Hero = () => (
  <section className="bg-black text-white border-b-2 border-black" data-testid="poker-hero">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-16 pb-20 lg:pt-24 lg:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7">
        <motion.div
          className="inline-flex items-center gap-3 border-2 border-white px-4 py-2 mb-10"
          data-testid="poker-eyebrow"
          initial={{ opacity: 0, x: -32 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease: EASE }}
        >
          <span className="w-2.5 h-2.5 bg-[#E60000] inline-block flex-shrink-0" />
          <span className="text-[10px] sm:text-xs md:text-sm uppercase tracking-[0.12em] font-bold">
            The Anti Poker Addiction Project
          </span>
        </motion.div>
        <h1
          className="font-display uppercase leading-[0.95] tracking-wide text-5xl sm:text-7xl lg:text-[6.2vw]"
          data-testid="poker-hero-title"
        >
          {HERO_LINES.map((line, i) => (
            <MaskedLine key={line.text} delay={0.6 + i * 0.14}>
              {line.accent ? <span className="text-[#E60000]">{line.text}</span> : line.text}
            </MaskedLine>
          ))}
        </h1>
        <div className="overflow-hidden mt-8">
          <motion.p
            className="font-script leading-[1] text-4xl sm:text-5xl lg:text-6xl text-[#E60000]"
            data-testid="poker-hero-script"
            initial={{ y: "110%", rotate: 2 }}
            animate={{ y: "0%", rotate: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
          >
            And hooks only come out one way.
          </motion.p>
        </div>
        <motion.p
          className="mt-8 max-w-2xl text-lg lg:text-xl text-neutral-300 font-medium leading-relaxed"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.4, ease: EASE }}
        >
          Cash games at 2am. Tournaments that swallow Sundays. Apps that turned
          your phone into a casino that never closes. Poker sells itself as a
          skill game — and that's exactly what makes it the stickiest trap in
          gambling. This page is for the player who suspects the game stopped
          being a game a long time ago.
        </motion.p>
        <motion.div
          className="mt-10 flex flex-col sm:flex-row gap-4"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.55, ease: EASE }}
        >
          <motion.a
            href="#signs"
            data-testid="poker-cta-signs"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.96 }}
            className="inline-flex items-center justify-center gap-2 bg-[#E60000] text-white border-2 border-white px-8 py-4 font-bold uppercase tracking-widest text-sm"
          >
            Learn The Warning Signs <ArrowRight className="w-4 h-4" />
          </motion.a>
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.96 }}>
            <Link
              to="/#products"
              data-testid="poker-cta-book"
              className="inline-flex items-center justify-center w-full bg-transparent text-white border-2 border-white px-8 py-4 font-bold uppercase tracking-widest text-sm hover:bg-white hover:text-black transition-colors"
            >
              <BookOpen className="w-4 h-4 mr-2" /> Get The Book
            </Link>
          </motion.div>
        </motion.div>
      </div>
      <motion.div
        className="lg:col-span-5 relative"
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.1, delay: 0.9, ease: EASE }}
      >
        <div className="absolute inset-0 spotlight" aria-hidden="true" />
        <div className="relative border-2 border-white shadow-[14px_14px_0px_0px_#E60000] overflow-hidden">
          <img
            src="/cover-hero.jpg"
            alt="The Secret to Stop Poker Addiction — book cover"
            data-testid="poker-hero-image"
            className="w-full h-auto block"
          />
        </div>
      </motion.div>
    </div>
  </section>
);

const MarqueeBand = () => (
  <section className="bg-[#E60000] text-white border-b-2 border-black" data-testid="poker-marquee">
    <Marquee speed={30} gradient={false} className="py-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <span key={i} className="font-display text-5xl lg:text-7xl uppercase mx-8 flex items-center gap-8">
          Tilt Is Not A Strategy.
          <span className="text-outline-white">The Rake Always Wins.</span>
          Fold For The Last Time.
          <span className="w-3 h-3 bg-white inline-block" />
        </span>
      ))}
    </Marquee>
  </section>
);

const SIGNS = [
  { title: "3AM sessions", text: "\"One more tournament\" at 3am on a work night. The lobby never sleeps — and neither do you anymore." },
  { title: "Tilt chasing", text: "A bad beat and suddenly you're re-buying to punish the deck. That's not strategy. That's the hook pulling." },
  { title: "Reloading", text: "The deposit button knows you by name. The bankroll rules you swore by dissolved months ago." },
  { title: "Studying to justify", text: "Hours of training videos and hand reviews that mostly serve one purpose: a reason to keep playing." },
  { title: "Hiding the graph", text: "You talk about the winning sessions. The losses stay in a spreadsheet nobody else will ever see." },
  { title: "Life on pause", text: "Dinners, birthdays, your kid's game — traded for a final table that paid less than the buy-ins cost." },
  { title: "Every screen is a table", text: "Phone at red lights. Tablet in bed. Laptop at work. The app is always one tap away — by design." },
  { title: "Quitting never sticks", text: "You've deleted the apps before. They always come back. A hobby doesn't do that. An addiction does." },
];

const Signs = () => (
  <section id="signs" className="bg-white border-b-2 border-black" data-testid="poker-signs">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
      <Reveal>
        <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#E60000] mb-4">Step 01 — See it clearly</div>
        <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase leading-[0.9] max-w-4xl">
          The warning <span className="text-[#E60000]">signs</span> at the table.
        </h2>
        <p className="mt-6 max-w-2xl text-lg text-neutral-700 font-medium">
          Poker players are the best in the world at one thing: rationalizing.
          Read these like an opponent reads you — honestly, looking for the tell.
        </p>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-2 border-black" data-testid="poker-signs-grid">
        {SIGNS.map((s, i) => (
          <Reveal key={s.title} delay={(i % 4) * 0.08} className={`border-black ${i % 4 !== 3 ? "lg:border-r-2" : ""} ${i % 2 === 0 ? "sm:border-r-2" : ""} border-b-2 ${i >= 4 ? "lg:border-b-0" : ""} ${i >= 6 ? "sm:border-b-0" : ""}`}>
            <div className="p-6 lg:p-8 h-full" data-testid={`poker-sign-${i}`}>
              <div className="font-display text-4xl text-[#E60000]">{String(i + 1).padStart(2, "0")}</div>
              <h3 className="font-display text-2xl uppercase mt-3 leading-tight">{s.title}</h3>
              <p className="mt-3 text-sm text-neutral-700 font-medium leading-relaxed">{s.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Trap = () => (
  <section className="bg-[#F5F2EC] border-b-2 border-black" data-testid="poker-trap">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
      <div className="lg:col-span-7">
        <Reveal>
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#E60000] mb-4">Step 02 — The illusion</div>
          <h2 className="font-display text-5xl sm:text-6xl uppercase leading-[0.9]">
            "It's a skill game."<br /><span className="text-[#E60000]">That's the trap.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-8 space-y-5 text-base lg:text-lg font-medium text-neutral-800 max-w-2xl leading-relaxed">
            <p>
              Poker is the only addiction that comes with a cover story. Slots
              players know the machine is rigged. Poker players get to believe
              they're <span className="italic">one adjustment away</span> — one
              course, one solver, one coach away from finally beating it.
            </p>
            <p>
              Here's the math nobody at the table says out loud: yes, a tiny
              fraction of players win long-term. For everyone else,{" "}
              <span className="font-bold text-black">
                variance keeps hope alive while the rake quietly eats the bankroll.
              </span>{" "}
              The ecosystem doesn't need you to lose fast — it needs you to lose
              slowly, forever, believing next month is your month.
            </p>
            <p>
              I know because I lived it for thirty years. I wasn't fish or shark —
              I was the fuel the whole game runs on. And the day I admitted{" "}
              <span className="font-bold text-[#E60000] uppercase tracking-wide">
                I was sick, not unlucky,
              </span>{" "}
              was the day my real recovery started.
            </p>
          </div>
        </Reveal>
      </div>
      <div className="lg:col-span-5 flex items-center">
        <Reveal delay={0.2} className="w-full">
          <div className="border-2 border-black bg-black text-white p-8 lg:p-10 shadow-brutal-red" data-testid="poker-trap-quote">
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#E60000] mb-4">From someone who lived it</div>
            <p className="font-display text-2xl lg:text-3xl uppercase leading-tight">
              "I told myself I was one adjustment away. For thirty years. The
              only adjustment that ever worked was walking away."
            </p>
            <div className="mt-6 text-xs uppercase tracking-widest font-bold text-neutral-400">
              — The author, 30 years in the cycle, now free
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const STEPS = [
  { n: "01", title: "Say it out loud", text: "'I have a poker problem.' Not a downswing. Not bad luck. A problem. Naming it is the moment it starts losing power over you." },
  { n: "02", title: "Tell one person", text: "One human being who loves you. The poker world runs on image — break it. You'll be shocked how many people answer with love, not judgment." },
  { n: "03", title: "Block the access", text: "Self-exclude from every site, delete every app, close the accounts, hand the cards to someone you trust. Willpower loses to one-tap access — remove the door." },
  { n: "04", title: "Call the helpline", text: "In the US: 1-800-GAMBLER — free, confidential, 24/7. Poker players call it every day. They have heard everything before." },
  { n: "05", title: "Read the field manual", text: "The Secret to Stop Poker Addiction was written from inside thirty years of the cycle — the exact scripts, resets, and the secret that finally broke it. Free download, in your inbox tonight." },
];

const Help = () => (
  <section id="help" className="bg-black text-white border-b-2 border-black" data-testid="poker-help">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
      <Reveal>
        <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#E60000] mb-4">Step 03 — The way out</div>
        <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase leading-[0.9]">
          Five moves.<br /><span className="text-[#E60000]">Starting tonight.</span>
        </h2>
      </Reveal>
      <div className="mt-14 border-2 border-white">
        {STEPS.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.05}>
            <div
              className={`grid grid-cols-12 gap-4 p-6 lg:p-8 items-start ${i > 0 ? "border-t-2 border-white" : ""}`}
              data-testid={`poker-step-${i}`}
            >
              <div className="col-span-3 sm:col-span-2 font-display text-4xl lg:text-5xl text-[#E60000]">{s.n}</div>
              <div className="col-span-9 sm:col-span-10">
                <h3 className="font-display text-2xl lg:text-3xl uppercase">{s.title}</h3>
                <p className="mt-2 text-neutral-300 font-medium leading-relaxed max-w-3xl">{s.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.15}>
        <div className="mt-10 border-2 border-white p-6 lg:p-8 flex flex-col md:flex-row items-start md:items-center gap-6" data-testid="poker-helpline-card">
          <div className="border-2 border-[#E60000] p-4 flex-shrink-0">
            <Phone className="w-8 h-8 text-[#E60000]" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#E60000]">In crisis or on the edge right now?</div>
            <div className="font-display text-3xl lg:text-4xl mt-1">1-800-GAMBLER <span className="text-lg text-neutral-400">(1-800-426-2537)</span></div>
            <p className="mt-2 text-sm text-neutral-400 font-medium">Free. Confidential. 24/7. United States. If you're elsewhere, your country has one too — search "gambling helpline" + your country.</p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const CrossLinks = () => (
  <section className="bg-white border-b-2 border-black" data-testid="poker-crosslinks">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
      <Reveal>
        <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase leading-[0.9]">
          Fold for the <span className="text-[#E60000]">last time.</span>
        </h2>
        <p className="mt-6 max-w-2xl text-lg text-neutral-700 font-medium">
          This page is one initiative of The Last Bet Project — a small,
          independent recovery project funded by readers and donors, not casinos.
        </p>
      </Reveal>
      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 border-2 border-black">
        <Reveal delay={0.05} className="border-b-2 md:border-b-0 md:border-r-2 border-black">
          <Link to="/#products" className="block h-full hover:bg-black hover:text-white transition-colors group" data-testid="poker-link-book">
            <div className="aspect-[16/10] w-full overflow-hidden border-b-2 border-black bg-black">
              <img src="/cover-hero.jpg" alt="The Secret to Stop Poker Addiction" className="w-full h-full object-cover" />
            </div>
            <div className="p-8">
              <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#E60000]">The field manual</div>
              <div className="font-display text-3xl uppercase mt-3 leading-tight">The Secret to Stop Poker Addiction</div>
              <p className="mt-3 text-sm font-medium text-neutral-600 group-hover:text-neutral-300">12 chapters, craving-defeat scripts, financial reset workbook. Free — in your inbox tonight.</p>
              <div className="mt-6 inline-flex items-center gap-2 font-bold uppercase tracking-widest text-xs">Get the book <ArrowRight className="w-4 h-4" /></div>
            </div>
          </Link>
        </Reveal>
        <Reveal delay={0.1} className="border-b-2 md:border-b-0 md:border-r-2 border-black">
          <Link to="/#author" className="block p-8 lg:p-10 h-full hover:bg-black hover:text-white transition-colors group" data-testid="poker-link-story">
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#E60000]">The author's story</div>
            <div className="font-display text-3xl uppercase mt-3 leading-tight">I lost it all so you don't have to</div>
            <p className="mt-3 text-sm font-medium text-neutral-600 group-hover:text-neutral-300">Homelessness, relapses, rock bottom — and the secret that finally broke the cycle.</p>
            <div className="mt-6 inline-flex items-center gap-2 font-bold uppercase tracking-widest text-xs">Read it <ArrowRight className="w-4 h-4" /></div>
          </Link>
        </Reveal>
        <Reveal delay={0.15}>
          <Link to="/gambling" className="block p-8 lg:p-10 h-full bg-black text-white hover:bg-[#E60000] transition-colors" data-testid="poker-link-gambling">
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#E60000]">Sister project</div>
            <div className="font-display text-3xl uppercase mt-3 leading-tight">The Anti Gambling Addiction Project</div>
            <p className="mt-3 text-sm font-medium text-neutral-300">Slots, sports betting, blackjack, roulette — the whole machine, exposed.</p>
            <div className="mt-6 inline-flex items-center gap-2 font-bold uppercase tracking-widest text-xs">Visit <ArrowRight className="w-4 h-4" /></div>
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="bg-[#0A0A0A] text-white" data-testid="poker-footer">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <LogoMark size={26} dark />
          <span className="font-display text-lg uppercase">The Last Bet Project</span>
          <span className="text-xs uppercase tracking-widest font-bold text-neutral-500">/ Anti Poker Addiction Project</span>
        </div>
        <div className="flex items-center gap-6 text-xs uppercase tracking-widest font-bold text-neutral-500">
          <Link to="/privacy" data-testid="poker-footer-privacy" className="hover:text-[#E60000] transition-colors">Privacy</Link>
          <Link to="/terms" data-testid="poker-footer-terms" className="hover:text-[#E60000] transition-colors">Terms</Link>
          <Link to="/" data-testid="poker-footer-home" className="hover:text-[#E60000] transition-colors">Main Site</Link>
        </div>
      </div>
      <div className="mt-6 pt-6 border-t-2 border-white/20 text-center text-xs text-neutral-500 uppercase tracking-widest font-bold">
        If you're in crisis, call 1-800-GAMBLER (US) or your local helpline.
      </div>
    </div>
  </footer>
);

export default function Poker() {
  return (
    <div className="min-h-screen bg-white font-body text-black" data-testid="poker-page">
      <Header />
      <Hero />
      <MarqueeBand />
      <Signs />
      <Trap />
      <Help />
      <FreeBookClaim
        packageId="ebook"
        title="The Secret to Stop Poker Addiction"
        cover="/cover-hero.jpg"
        tagline="The manual to crush the urge, rebuild your bankroll-of-life, and never go back to the felt — written from inside thirty years of the cycle."
      />
      <CrossLinks />
      <Footer />
    </div>
  );
}
